import { useState, useRef, RefObject, useCallback } from 'react'
import { haptics } from '@/utils/haptics'

interface UsePullToRefreshOptions {
  onRefresh: () => Promise<void>
  threshold?: number
  maxPullDistance?: number
  enabled?: boolean
}

export function usePullToRefresh(
  scrollRef: RefObject<HTMLDivElement>,
  options: UsePullToRefreshOptions
) {
  const { onRefresh, threshold = 80, maxPullDistance = 120, enabled = true } = options
  
  const [pullDistance, setPullDistance] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [canPull, setCanPull] = useState(false)
  
  const touchStart = useRef<{ y: number; scrollTop: number } | null>(null)
  const isTriggered = useRef(false)

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!enabled || !scrollRef.current) return
    
    const scrollTop = scrollRef.current.scrollTop
    
    // Solo permitir pull si está en el top
    if (scrollTop === 0) {
      touchStart.current = {
        y: e.touches[0].clientY,
        scrollTop,
      }
      setCanPull(true)
    }
  }, [enabled, scrollRef])

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!enabled || !canPull || !touchStart.current || !scrollRef.current) return
    
    const currentY = e.touches[0].clientY
    const deltaY = currentY - touchStart.current.y
    
    // Solo pull hacia abajo y si está en el top
    if (deltaY > 0 && scrollRef.current.scrollTop === 0) {
      e.preventDefault()
      
      // Aplicar resistencia
      const resistance = 0.5
      const distance = Math.min(deltaY * resistance, maxPullDistance)
      
      setPullDistance(distance)
      
      // Haptic feedback al alcanzar threshold
      if (distance >= threshold && !isTriggered.current) {
        haptics.impact('medium')
        isTriggered.current = true
      } else if (distance < threshold && isTriggered.current) {
        isTriggered.current = false
      }
    }
  }, [enabled, canPull, threshold, maxPullDistance, scrollRef])

  const handleTouchEnd = useCallback(async () => {
    if (!enabled || !canPull) return
    
    setCanPull(false)
    touchStart.current = null
    
    if (pullDistance >= threshold && !isRefreshing) {
      setIsRefreshing(true)
      haptics.notification('success')
      
      try {
        await onRefresh()
      } catch (error) {
        console.error('Refresh error:', error)
        haptics.notification('error')
      } finally {
        setIsRefreshing(false)
        setPullDistance(0)
        isTriggered.current = false
      }
    } else {
      setPullDistance(0)
      isTriggered.current = false
    }
  }, [enabled, canPull, pullDistance, threshold, isRefreshing, onRefresh])

  const getRefreshProgress = () => {
    return Math.min(pullDistance / threshold, 1)
  }

  const shouldTrigger = () => {
    return pullDistance >= threshold
  }

  return {
    pullDistance,
    isRefreshing,
    getRefreshProgress,
    shouldTrigger,
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
  }
}
